import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API } from "@/services/API";

const initialState = {
  quotes: {
    data: null,
    loading: false,
    error: null,
  },
  bookings: {
    data: null,
    loading: false,
    error: null,
  },
  changedCounts: {
    pending: 0,
    accepted: 0,
    declined: 0,
  },
};

export const fetchQuotes = createAsyncThunk("offers/fetch-quotes", async ({ isAutoRefresh = false } = {}, { rejectWithValue }) => {
  return API.get("/parade/quote")
    .then((res) => {
      return { data: res, isAutoRefresh };
    })
    .catch((err) => {
      console.error("Failed to fetch quotes", err);
      return rejectWithValue(err);
    });
});

export const fetchBookings = createAsyncThunk("offers/fetch-bookings", async ({ isAutoRefresh = false } = {}, { rejectWithValue }) => {
  return API.get("/parade/book")
    .then((res) => {
      return { data: res, isAutoRefresh };
    })
    .catch((err) => {
      console.error("Failed to fetch bookings", err);
      return rejectWithValue(err);
    });
});

export const fetchAllOffers = createAsyncThunk("offers/fetch-all", async ({ isAutoRefresh = false } = {}, { dispatch }) => {
  await Promise.all([dispatch(fetchQuotes({ isAutoRefresh })), dispatch(fetchBookings({ isAutoRefresh }))]);
});

// Helper function to count changes by category
const countChanges = (oldData, newData, type) => {
  const counts = { pending: 0, accepted: 0, declined: 0 };

  if (!oldData || !newData) return counts;

  // Create maps for quick lookup
  const oldMap = new Map(oldData.map((item) => [item.id, item]));

  // Check for new or changed items
  newData.forEach((newItem) => {
    const oldItem = oldMap.get(newItem.id);

    // Detect new items or status changes
    const isNewItem = !oldItem;
    const statusChanged = isNewItem || oldItem.status !== newItem.status;

    // Debug logging for all items
    console.log(`[countChanges] Checking ${type} ${newItem.id}:`, {
      isNew: isNewItem,
      oldStatus: oldItem?.status,
      newStatus: newItem.status,
      changed: statusChanged,
    });

    if (statusChanged) {
      // Categorize by status
      const status = newItem.status;
      const isQuote = type === "quote";
      const isBooking = type === "booking";

      // Log for debugging
      if (isNewItem) {
        console.log(`[countChanges] ✅ New ${type} detected:`, newItem.id, "Status:", status);
      } else {
        console.log(`[countChanges] ✅ Status changed for ${type}:`, newItem.id, `${oldItem.status} → ${status}`);
      }

      // Pending: PENDING or NOT_SUBMITTED for both quotes and bookings
      if (["PENDING", "NOT_SUBMITTED"].includes(status)) {
        counts.pending++;
      }
      // Accepted: ACCEPTED for quotes, CONFIRMED for bookings
      else if ((isQuote && status === "ACCEPTED") || (isBooking && status === "CONFIRMED")) {
        counts.accepted++;
      }
      // Declined: DECLINED/CANCELLED/EXPIRED for quotes, REJECTED/CANCELLED for bookings
      else if (
        (isQuote && ["DECLINED", "CANCELLED", "EXPIRED"].includes(status)) ||
        (isBooking && ["REJECTED", "CANCELLED"].includes(status))
      ) {
        counts.declined++;
      } else {
        // Log statuses that don't match any category
        console.warn(`[countChanges] Unhandled status for ${type}:`, status, "Item ID:", newItem.id);
      }
    }
  });

  return counts;
};

const offersSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {
    clearOffersState: () => {
      return initialState;
    },
    clearChangedCounts: (state, action) => {
      const tab = action.payload; // 'pending', 'accepted', or 'declined'
      if (tab && state.changedCounts[tab] !== undefined) {
        state.changedCounts[tab] = 0;
      }
    },
    updateOfferStatus: (state, action) => {
      const { offerId, offerType, newStatus, incrementCounter } = action.payload;

      if (offerType === "quote" && state.quotes.data) {
        const quote = state.quotes.data.find((q) => q.id === offerId);
        if (quote) {
          const oldStatus = quote.status;
          quote.status = newStatus;

          // Increment notification counter if requested
          if (incrementCounter) {
            // Check if status moved to declined category
            if (!["DECLINED", "CANCELLED", "EXPIRED"].includes(oldStatus) && ["DECLINED", "CANCELLED", "EXPIRED"].includes(newStatus)) {
              state.changedCounts.declined++;
            }
            // Check if status moved to accepted category
            else if (oldStatus !== "ACCEPTED" && newStatus === "ACCEPTED") {
              state.changedCounts.accepted++;
            }
          }
        }
      } else if (offerType === "booking" && state.bookings.data) {
        const booking = state.bookings.data.find((b) => b.id === offerId);
        if (booking) {
          const oldStatus = booking.status;
          booking.status = newStatus;

          // Increment notification counter if requested
          if (incrementCounter) {
            // Check if status moved to declined category
            if (!["REJECTED", "CANCELLED"].includes(oldStatus) && ["REJECTED", "CANCELLED"].includes(newStatus)) {
              state.changedCounts.declined++;
            }
            // Check if status moved to accepted category
            else if (oldStatus !== "CONFIRMED" && newStatus === "CONFIRMED") {
              state.changedCounts.accepted++;
            }
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Quotes
      .addCase(fetchQuotes.pending, (state, action) => {
        const isAutoRefresh = action.meta.arg?.isAutoRefresh;
        state.quotes.loading = !isAutoRefresh;
        state.quotes.error = null;
      })
      .addCase(fetchQuotes.fulfilled, (state, action) => {
        const { data: newData, isAutoRefresh } = action.payload;
        const hasDataChanged = JSON.stringify(state.quotes.data) !== JSON.stringify(newData);

        if (hasDataChanged) {
          // Count changes if it's an auto refresh
          if (isAutoRefresh && state.quotes.data) {
            const quoteCounts = countChanges(state.quotes.data, newData, "quote");
            state.changedCounts.pending += quoteCounts.pending;
            state.changedCounts.accepted += quoteCounts.accepted;
            state.changedCounts.declined += quoteCounts.declined;
          }

          state.quotes.data = newData;
          state.quotes.error = null;
        }

        state.quotes.loading = false;
      })
      .addCase(fetchQuotes.rejected, (state, action) => {
        state.quotes = {
          data: null,
          loading: false,
          error: action.payload || "Error fetching quotes",
        };
      })
      // Bookings
      .addCase(fetchBookings.pending, (state, action) => {
        const isAutoRefresh = action.meta.arg?.isAutoRefresh;
        state.bookings.loading = !isAutoRefresh;
        state.bookings.error = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        const { data: newData, isAutoRefresh } = action.payload;
        const hasDataChanged = JSON.stringify(state.bookings.data) !== JSON.stringify(newData);

        if (hasDataChanged) {
          // Count changes if it's an auto refresh
          if (isAutoRefresh && state.bookings.data) {
            const bookingCounts = countChanges(state.bookings.data, newData, "booking");
            state.changedCounts.pending += bookingCounts.pending;
            state.changedCounts.accepted += bookingCounts.accepted;
            state.changedCounts.declined += bookingCounts.declined;
          }

          state.bookings.data = newData;
          state.bookings.error = null;
        }

        state.bookings.loading = false;
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.bookings = {
          data: null,
          loading: false,
          error: action.payload || "Error fetching bookings",
        };
      });
  },
});

export const { clearOffersState, clearChangedCounts, updateOfferStatus } = offersSlice.actions;

export default offersSlice.reducer;
